import { useMutation, useQueryClient } from "react-query";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";
import { useContactId } from "./useContactId";

export function useOrderBuyAgain() {
  const contactId = useContactId();
  const queryClient = useQueryClient();
  return useMutation(
    async (orderIdInput) => {
      if (contactId == null) {
        throw new Error("Buy again failed: missing contactId");
      }
      const orderId =
        typeof orderIdInput === "object" && orderIdInput !== null
          ? orderIdInput.orderId
          : orderIdInput;
      const res = await makeAuthedRequest(`/orders/${orderId}/buy-again`, {
        method: "POST",
        body: JSON.stringify({ contactId }),
      });
      if (!res.ok) throw await parseApiError(res, "Buy again failed");
      const data = await res.json();

      // The backend replays each item individually and skips ones that are
      // now inactive or no longer visible to this IBO rather than aborting,
      // so a 201 here does not guarantee anything actually landed in the
      // cart. When every item was skipped, the cart is unchanged: surface
      // this as a failure (not a silent no-op) so callers that navigate to
      // /cart on success dont do so, and the global error modal shows why.
      const skippedCount =
        (data?.skippedProductIds?.length ?? 0) +
        (data?.skippedSkus?.length ?? 0);
      if ((data?.itemsAdded ?? 0) === 0 && skippedCount > 0) {
        const error = new Error("Buy again failed: no items could be re-added");
        error.status = 409;
        error.serverMessage =
          data.message ??
          "These items are no longer available and could not be re-ordered.";
        throw error;
      }

      return data;
    },
    { onSuccess: () => queryClient.invalidateQueries("cart") }
  );
}

export function useDeleteOrder() {
  const queryClient = useQueryClient();
  return useMutation(
    async (orderId) => {
      const res = await makeAuthedRequest(`/orders/${orderId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw await parseApiError(res, "Delete order failed");
    },
    { onSuccess: () => queryClient.invalidateQueries("orders") }
  );
}

export function useUpdateOrderShipping() {
  const contactId = useContactId();
  const queryClient = useQueryClient();
  return useMutation(
    async ({ orderId, ...payload }) => {
      const shipAddress = payload?.shipAddress ?? {
        address1: payload?.address1 ?? payload?.street ?? payload?.line1 ?? "",
        address2:
          payload?.address2 ?? payload?.street2 ?? payload?.line2 ?? null,
        city: payload?.city ?? "",
        state: payload?.state ?? "",
        postalCode:
          payload?.postalCode ?? payload?.zip ?? payload?.zipCode ?? "",
        country: payload?.country ?? "US",
      };

      const body = {
        contactId,
        shipAddress,
      };

      const res = await makeAuthedRequest(`/orders/${orderId}/shipping`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await parseApiError(res, "Update shipping failed");
      return res.json();
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries("orders");
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === "order" &&
            String(query.queryKey[1]) === String(variables?.orderId),
        });
      },
    }
  );
}

export function useOrderSupport() {
  return useMutation(async ({ orderId, ...payload }) => {
    const res = await makeAuthedRequest(`/orders/${orderId}/support`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw await parseApiError(res, "Order support failed");
    return res.json();
  });
}
